import { type ComponentType, useEffect, useMemo } from 'react';
import {
  isRouteErrorResponse,
  Navigate,
  Outlet,
  useRouteError,
} from 'react-router-dom';

// Layout & top-level pages
import { BaseLayout } from '@/components/architecture/Layout/BaseLayout';
import { ClientDetailsLayout } from '@/components/architecture/Layout/ClientDetailsLayout';
import { NoSidebarLayout } from '@/components/architecture/Layout/NoSidebarLayout';
import { PrimaryLayout } from '@/components/architecture/Layout/PrimaryLayout';
import { PrintOnlyLayout } from '@/components/architecture/Layout/PrintOnlyLayout';
import { FeedbackProvider } from '@/components/notifications/Feedback/FeedbackProvider';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { HouseholdFormRoutes } from '@/modules/forms/HouseholdFormRoutes';
import { TrowserProvider } from '@/modules/navigation/trowsers/Trowser.provider';
import { NotificationRedirect } from '@/modules/notifications/NotificationRedirect/NotificationRedirect';
import { CurrentUserWorkflowTrackersContextProvider } from '@/modules/workflowTrackers/contexts/CurrentUserWorkflowTrackersContext.provider';
import { AdminAuditLogPage } from '@/pages/admin/AdminAuditLogPage/AdminAuditLogPage';
import { AdminBrandingPage } from '@/pages/admin/AdminBrandingPage/AdminBrandingPage';
import { AdminCategoriesConfigurationPage } from '@/pages/admin/AdminCategoriesConfigurationPage/AdminCategoriesConfigurationPage';
import { AdminConfigurationsPage } from '@/pages/admin/AdminConfigurationsPage/AdminConfigurationsPage';
import { AdminDocumentSummariesReportPage } from '@/pages/admin/AdminDocumentSummariesReportPage/AdminDocumentSummariesReportPage';
import { AdminFeatureFlagsPage } from '@/pages/admin/AdminFeatureFlagsPage/AdminFeatureFlagsPage';
import { AdminFormEditorPage } from '@/pages/admin/AdminFormsPage/AdminFormEditorPage';
import { AdminFormsPage } from '@/pages/admin/AdminFormsPage/AdminFormsPage';
import { AdminFormsRoutes } from '@/pages/admin/AdminFormsPage/AdminFormsRoutes';
import { AdminFormTestRunPage } from '@/pages/admin/AdminFormsPage/AdminFormTestRunPage';
import { AdminFormTestsPage } from '@/pages/admin/AdminFormsPage/AdminFormTestsPage';
import { AdminFormTestSubmissionPage } from '@/pages/admin/AdminFormsPage/AdminFormTestSubmissionPage';
import { AdminIntegrationsPage } from '@/pages/admin/AdminIntegrationsPage/AdminIntegrationsPage';
import { ManagedSFTPFilesPage } from '@/pages/admin/AdminIntegrationsPage/ManagedSFTPFilesPage';
import { AdminLegalDisclaimersPage } from '@/pages/admin/AdminLegalDisclaimersPage/AdminLegalDisclaimersPage';
import { AdminOrganizationsPage } from '@/pages/admin/AdminOrganizationsPage/AdminOrganizationsPage';
// Admin pages
import { AdminPage } from '@/pages/admin/AdminPage';
import { AdminPresentationTemplateEditorPage } from '@/pages/admin/AdminPresentationTemplateEditorPage/AdminPresentationTemplateEditorPage';
import { AdminPresentationTemplatesPage } from '@/pages/admin/AdminPresentationTemplatesPage/AdminPresentationTemplatesPages';
import { AdminPromptIterationPage } from '@/pages/admin/AdminPromptIterationPage';
import { AdminRolesPage } from '@/pages/admin/AdminRolesPage';
import { AdminSecurityPage } from '@/pages/admin/AdminSecurityPage/AdminSecurityPage';
import { AdminSubBrandsPage } from '@/pages/admin/AdminSubBrandsPage/AdminSubBrandsPage';
import { AdminSummaryPromptsPage } from '@/pages/admin/AdminSummaryPromptsPage/AdminSummaryPromptsPage';
import { AdminTeamsPage } from '@/pages/admin/AdminTeamsPage/AdminTeamsPage';
import { AdminTenantsPage } from '@/pages/admin/AdminTenantsPage/AdminTenantsPage';
import { AdminEducationPage } from '@/pages/admin/AdminUsersPage/AdminEducationPagesTab';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage/AdminUsersPage';
import {
  ClientDetailsArtifactBuilderPage,
  ClientDetailsArtifactStudioPage,
} from '@/pages/ClientDetailsPage/ClientDetailsArtifactBuilderPage';
import { ClientDetailsDocumentsPage } from '@/pages/ClientDetailsPage/ClientDetailsDocumentsPage/ClientDetailsDocumentsPage';
import { ClientDetailsMemosPage } from '@/pages/ClientDetailsPage/ClientDetailsDocumentsPage/ClientDetailsMemosPage';
import { ClientDetailsManageIncomeExpensesPage } from '@/pages/ClientDetailsPage/ClientDetailsEntitiesPage/ClientDetailsManageIncomeExpensesPage/ClientDetailsManageIncomeExpensesPage';
import { ClientDetailsManageLiabilitiesPage } from '@/pages/ClientDetailsPage/ClientDetailsEntitiesPage/ClientDetailsManageLiabilitiesPage/ClientDetailsManageLiabilitiesPage';
import { ClientFamilyTreePage } from '@/pages/ClientDetailsPage/ClientDetailsPeopleOrganizationPage/ClientFamilyTreePage/ClientFamilyTreePage';
import { ClientProfessionalTeam } from '@/pages/ClientDetailsPage/ClientDetailsPeopleOrganizationPage/ClientProfessionalTeam/ClientProfessionalTeam';
import { ClientPeopleListView } from '@/pages/ClientDetailsPage/ClientDetailsPeopleOrganizationPage/ListView/ClientPeopleListView';
import { ClientDetailsPerformancePage } from '@/pages/ClientDetailsPage/ClientDetailsPerformancePage/ClientDetailsPerformancePage';
import { ClientDetailsTasksPage } from '@/pages/ClientDetailsPage/ClientDetailsTasksPage';
// Client pages
import { ClientListPage } from '@/pages/ClientListPage/ClientListPage';
import { ClientCashflowsPage } from '@/pages/ClientPages/ClientCashflowsPage/ClientCashflowsPage';
import { ClientDocumentsPage } from '@/pages/ClientPages/ClientDocumentsPage';
import { ClientEstateBalanceSheetPage } from '@/pages/ClientPages/ClientEstatePage/ClientEstateBalanceSheetPage/ClientEstateBalanceSheetPage';
import { ClientEstateBusinessesPage } from '@/pages/ClientPages/ClientEstatePage/ClientEstateBusinessesPage/ClientEstateBusinessesPage';
import { ClientEstateDirectHoldingsPage } from '@/pages/ClientPages/ClientEstatePage/ClientEstateDirectHoldingsPage/ClientEstateDirectHoldingsPage';
import { ClientEstateEntitiesPage } from '@/pages/ClientPages/ClientEstatePage/ClientEstateEntitiesPage/ClientEstateEntitiesPage';
import { ClientEstateLifeInsurancePage } from '@/pages/ClientPages/ClientEstatePage/ClientEstateLifeInsurancePage/ClientEstateLifeInsurancePage';
import { ClientEstatePage } from '@/pages/ClientPages/ClientEstatePage/ClientEstatePage';
import { ClientEstateTrustsPage } from '@/pages/ClientPages/ClientEstatePage/ClientEstateTrustsPage/ClientEstateTrustsPage';
import { ClientGiftingPage } from '@/pages/ClientPages/ClientGiftingPage/ClientGiftingPage';
import { ClientOverviewPage } from '@/pages/ClientPages/ClientOverviewPage/ClientOverviewPage';
import { ClientOwnershipPage } from '@/pages/ClientPages/ClientOwnershipPage/ClientOwnershipPage';
import { ClientPeoplePage } from '@/pages/ClientPages/ClientPeoplePage/ClientPeoplePage';
import { ClientTasksPage } from '@/pages/ClientPages/ClientTasksPage';
import { CollaboratorsPage } from '@/pages/collaborators/CollaboratorsPage';
import { CharitableTrustDesignerAnalysis } from '@/pages/designer/CharitableTrustDesigner/CharitableTrustDesignerAnalysis/CharitableTrustDesignerAnalysis';
import { CharitableTrustDesignerBasicInformation } from '@/pages/designer/CharitableTrustDesigner/CharitableTrustDesignerBasicInformation/CharitableTrustDesignerBasicInformation';
import { CharitableTrustDesignerPage } from '@/pages/designer/CharitableTrustDesigner/CharitableTrustDesignerPage';
import { DesignerPageV2 } from '@/pages/designer/DesignerPage';
import { GiftDesignerBasicInformationPage } from '@/pages/designer/GiftDesigner/GiftDesignerBasicInformationPage';
import { GiftDesignerModelScenariosPage } from '@/pages/designer/GiftDesigner/GiftDesignerModelScenariosPage';
import { GiftDesignerPage } from '@/pages/designer/GiftDesigner/GiftDesignerPage';
import { DocumentDetailsPage } from '@/pages/DocumentDetailsPage/DocumentDetailsPage';
import { DocumentDetailsPageVariant } from '@/pages/DocumentDetailsPage/DocumentDetailsPage.types';
import { EducationPage } from '@/pages/education/EducationPage';
import { EntityDetailPage } from '@/pages/entity/EntityDetailPage';
import { EntityDocumentsPage } from '@/pages/entity/EntityDocumentsPage';
import { EntityPage } from '@/pages/entity/EntityPage';
import { EntityPaymentsPage } from '@/pages/entity/EntityPaymentsPage/EntityPaymentsPage';
import { EntityPerformancePage } from '@/pages/entity/EntityPerformancePage/EntityPerformancePage';
import { EntityTasksPage } from '@/pages/entity/EntityTasksPage/EntityTasksPage';
import { EntityValuationHistoryPage } from '@/pages/entity/EntityValuationHistoryPage/EntityValuationHistoryPage';
// Entity & trust pages
import { ErrorCodes } from '@/pages/error/constants';
import { ErrorOverlay } from '@/pages/error/ErrorOverlay';
import { PublicErrorPage } from '@/pages/error/PublicErrorPage';
import { ImplementationPage } from '@/pages/implementation/ImplementationPage';
import { ClientAccessPage } from '@/pages/manage/editClient/ClientAccessPage';
// Internal pages
// Client management pages
import { ClientActivityHistoryPage } from '@/pages/manage/editClient/ClientActivityHistoryPage/ClientActivityHistoryPage';
import { KeyDetailsPage } from '@/pages/manage/editClient/KeyDetailsPage/KeyDetailsPage';
import { MemoDetailsPage } from '@/pages/MemoDetailsPage/MemoDetailsPage';
import { ProposalBuilderPage } from '@/pages/proposal/page/ProposalBuilderPage';
import { StandaloneTaskPage } from '@/pages/task/StandaloneTaskPage';
import { TasksPage } from '@/pages/tasks/TasksPage';
// User pages
import { UserProfilePage } from '@/pages/user/UserProfilePage';
import { UserProfileSettingsPage } from '@/pages/user/UserProfileSettingsPage/UserProfileSettingsPage';

import { computeRoutes } from './computeRoutes';
import {
  AugmentedRoute,
  CharitableTrustDesignerStages,
  GiftDesignerStages,
  ROUTE_KEYS,
} from './constants';
import { FunctionalRedirect } from './FunctionalRedirect';
import { GuardedPageRedirect } from './GuardedPageRedirect';
import { withLazyRouteErrorBoundaries } from './LazyRouteErrorBoundary';
import { RootRedirect } from './RootRedirect';
import { CLIENT_PROPOSAL_PATH } from './routes.constants';

type ComponentExportName<TModule> = {
  [TKey in keyof TModule]-?: TModule[TKey] extends ComponentType ? TKey : never;
}[keyof TModule];

function lazyRoute<TModule>(
  loadModule: () => Promise<TModule>,
  componentName: ComponentExportName<TModule>
): NonNullable<AugmentedRoute['lazy']> {
  return async () => ({
    // ComponentExportName permits only exports that React Router can render.
    Component: (await loadModule())[componentName] as ComponentType,
  });
}

/**
 * @description This component is to only be used for the root error boundary. It will catch any errors that
 * are not caught in a child error boundary. If it triggers, you should probably add an error boundary to the
 * component that is throwing the error. See <Layout /> for an example of a component that has an error boundary.
 */
function RootErrorBoundary() {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const routeError = useRouteError();
  const isRouteError = isRouteErrorResponse(routeError);
  const errorCode = useMemo(() => {
    if (isRouteError && routeError.status === 404) {
      return ErrorCodes.NOT_FOUND;
    }

    return ErrorCodes.UNKNOWN;
  }, [isRouteError, routeError]);

  useEffect(() => {
    if (errorCode === ErrorCodes.UNKNOWN) {
      reportError('Client root error boundary triggered!', routeError as Error);
    }
  }, [errorCode, reportError, routeError, showFeedback]);

  useEffect(() => {
    if (errorCode === ErrorCodes.NOT_FOUND) return;
    trackUserEvent('error_page viewed', {
      errorCode,
      source: 'root_error_boundary',
    });
  }, [errorCode, trackUserEvent]);

  return (
    <ErrorOverlay
      heading="There has been an unexpected error"
      showLogo={false} // don't show the logo on a "our bad" error page
      showBackButton // but do give the user a way to go back
      body={
        <p>
          The Luminary team has automatically notified and is looking into it.
          Refreshing the page may resolve the issue. If the issue persists or
          for any concerns, please reach out to{' '}
          <a href="mailto:support@withluminary.com" style={{ color: 'white' }}>
            support@withluminary.com
          </a>
          .
        </p>
      }
    />
  );
}

// Keep export downloads and trowsers mounted across layout changes. Download
// feedback needs both the router and FeedbackProvider to render its alerts.
function AppRootLayout() {
  return (
    <FeedbackProvider>
      <CurrentUserWorkflowTrackersContextProvider>
        <TrowserProvider>
          <Outlet />
        </TrowserProvider>
      </CurrentUserWorkflowTrackersContextProvider>
    </FeedbackProvider>
  );
}

const appRoutes: AugmentedRoute[] = [
  {
    element: <BaseLayout />,
    key: ROUTE_KEYS.__CONTAINER,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        path: 'households',
        element: <PrimaryLayout />,
        key: ROUTE_KEYS.__CONTAINER,
        handle: {
          title: 'Clients',
        },
        children: [
          {
            index: true,
            path: '',
            element: <ClientListPage />,
            key: ROUTE_KEYS.HOUSEHOLDS_LIST,
          },
          {
            path: 'import',
            key: ROUTE_KEYS.__CONTAINER,
            children: [
              {
                path: 'bulk/entities/:flowType',
                key: ROUTE_KEYS.IMPORT_INTEGRATION_ENTITIES_BULK,
                lazy: lazyRoute(
                  () => import('@/pages/importEntities/BulkImportEntitiesPage'),
                  'BulkImportEntitiesPage'
                ),
                handle: {
                  title: 'Data import',
                },
              },
              {
                path: 'bulk/:flowType',
                key: ROUTE_KEYS.IMPORT_INTEGRATION_CLIENTS,
                lazy: lazyRoute(
                  () => import('@/pages/import/bulkImport/BulkImportPage'),
                  'BulkImportPage'
                ),
                handle: {
                  title: 'Data import',
                },
              },
              {
                path: 'csv-valuations',
                key: ROUTE_KEYS.IMPORT_CSV_VALUATIONS,
                lazy: lazyRoute(
                  () =>
                    import(
                      '@/pages/import/csvImport/ImportCSVValuationsPage/ImportCSVValuationsPage'
                    ),
                  'ImportCSVValuationsPage'
                ),
                handle: {
                  title: 'CSV valuations import',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'households',
        key: ROUTE_KEYS.__CONTAINER,
        handle: {
          title: 'Clients',
        },
        children: [
          {
            path: ':householdId',
            key: ROUTE_KEYS.__CONTAINER,
            element: <ClientDetailsLayout />,
            children: [
              {
                path: 'artifact-builder',
                key: ROUTE_KEYS.HOUSEHOLD_ARTIFACT_BUILDER,
                element: <ClientDetailsArtifactBuilderPage />,
                handle: { title: 'Artifact builder' },
                children: [
                  {
                    path: '',
                    index: true,
                    key: ROUTE_KEYS.__CONTAINER,
                    element: <ClientDetailsArtifactStudioPage />,
                  },
                  {
                    path: 'memos/:memoId',
                    key: ROUTE_KEYS.HOUSEHOLD_ARTIFACT_BUILDER_MEMO_DETAILS,
                    element: <MemoDetailsPage />,
                    handle: { title: 'Memo details' },
                  },
                ],
              },
              {
                path: 'details',
                key: ROUTE_KEYS.__CONTAINER,
                handle: {
                  title: 'Client Details',
                },
                children: [
                  {
                    path: 'overview',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
                    element: <ClientOverviewPage />,
                  },
                  {
                    path: 'cashflows',
                    key: ROUTE_KEYS.HOUSEHOLD_SIDEBAR_CASHFLOWS,
                    element: <ClientCashflowsPage />,
                    children: [
                      {
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.__CONTAINER,
                        element: <Navigate to="income-expenses" replace />,
                      },
                      {
                        path: 'income-expenses',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ENTITIES_MANAGE_INCOME_EXPENSES,
                        element: <ClientDetailsManageIncomeExpensesPage />,
                      },
                    ],
                  },
                  {
                    path: 'gifting',
                    key: ROUTE_KEYS.HOUSEHOLD_SIDEBAR_GIFTING,
                    element: <ClientGiftingPage />,
                    children: [
                      {
                        index: true,
                        path: '',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_GIFTING,
                        handle: {
                          title: 'Gifting',
                        },
                      },
                      {
                        path: 'estate-tax-calculation',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_GIFTING_ESTATE_TAX_CALCULATION_TROWSER,
                        lazy: lazyRoute(
                          () =>
                            import(
                              '@/modules/estateTaxScenarios/EstateTaxScenariosTrowser'
                            ),
                          'EstateTaxScenariosTrowserPage'
                        ),
                      },
                    ],
                  },
                  {
                    path: 'entities/:entityId',
                    key: ROUTE_KEYS.HOUSEHOLD_ENTITY_BASE,
                    element: <EntityPage />,
                    handle: {
                      title: 'Entities',
                    },
                    children: [
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
                        path: 'details',
                        element: <EntityDetailPage />,
                        handle: {
                          title: 'Entity Details',
                        },
                      },
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS_DIAGRAM,
                        path: 'diagram',
                        lazy: lazyRoute(
                          () => import('@/pages/entity/EntityDiagramPage'),
                          'EntityDiagramPage'
                        ),
                        handle: {
                          title: 'Diagram',
                        },
                      },
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_TASKS,
                        path: 'tasks',
                        element: <EntityTasksPage />,
                        handle: {
                          title: 'Entity Tasks',
                        },
                      },
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_PERFORMANCE,
                        path: 'performance',
                        element: <EntityPerformancePage />,
                        handle: {
                          title: 'Entity Performance',
                        },
                      },
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_VALUATION_HISTORY,
                        path: 'valuation',
                        element: <EntityValuationHistoryPage />,
                        handle: {
                          title: 'Valuation History',
                        },
                      },
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_PAYMENTS,
                        path: 'payments',
                        element: <EntityPaymentsPage />,
                        handle: {
                          title: 'Entity Payments',
                        },
                      },
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_DOCUMENTS,
                        path: 'documents',
                        element: <EntityDocumentsPage />,
                        handle: {
                          title: 'Entity Documents',
                        },
                      },
                      {
                        key: ROUTE_KEYS.HOUSEHOLD_ENTITY_DOCUMENT_DETAILS,
                        path: `documents/:documentId`,
                        element: (
                          <DocumentDetailsPage
                            variant={DocumentDetailsPageVariant.Entity}
                          />
                        ),
                        handle: {
                          title: 'Entity Documents',
                        },
                      },
                      {
                        // this is the catchall route, so that users who land on /households/:id/trust/:id will be
                        // redirected to this default path
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.SPECIFIC_HOUSEHOLD_REDIRECT,
                        element: <Navigate to={`details`} replace />,
                      },
                    ],
                  },
                  {
                    path: 'estate',
                    key: ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ESTATE,
                    element: <ClientEstatePage />,
                    handle: {
                      title: 'Estate',
                    },
                    children: [
                      {
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.__CONTAINER,
                        element: <Navigate to="balance-sheet" replace />,
                      },
                      {
                        path: 'balance-sheet',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_BALANCE_SHEET,
                        element: <ClientEstateBalanceSheetPage />,
                      },
                      {
                        path: 'direct-holdings',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_DIRECT_HOLDINGS,
                        element: <ClientEstateDirectHoldingsPage />,
                      },
                      {
                        path: 'businesses',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES,
                        element: <ClientEstateBusinessesPage />,
                      },
                      {
                        path: 'life-insurance',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_LIFE_INSURANCE,
                        element: <ClientEstateLifeInsurancePage />,
                      },
                      {
                        path: 'trusts',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS,
                        element: <ClientEstateTrustsPage />,
                      },
                      {
                        path: 'liabilities',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_ENTITIES_MANAGE_LIABILITIES,
                        element: <ClientDetailsManageLiabilitiesPage />,
                      },
                      {
                        path: 'manage-dispositions',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
                        lazy: lazyRoute(
                          () =>
                            import(
                              '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/ManageDispositionsPage'
                            ),
                          'ManageDispositionsPage'
                        ),
                        handle: {
                          title: 'Dispositions',
                        },
                        children: [
                          {
                            path: 'estate-tax-calculation',
                            key: ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS_ESTATE_TAX_CALCULATION_TROWSER,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/modules/estateTaxScenarios/EstateTaxScenariosTrowser'
                                ),
                              'EstateTaxScenariosTrowserPage'
                            ),
                          },
                        ],
                      },
                      {
                        path: 'entities',
                        key: ROUTE_KEYS.HOUSEHOLD_ESTATE_ENTITIES,
                        element: <ClientEstateEntitiesPage />,
                      },
                    ],
                  },
                  {
                    path: 'people',
                    key: ROUTE_KEYS.HOUSEHOLD_SIDEBAR_PEOPLE,
                    element: <ClientPeoplePage />,
                    children: [
                      {
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.__CONTAINER,
                        element: (
                          <Navigate to="individuals-organizations" replace />
                        ),
                      },
                      {
                        path: 'individuals-organizations',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PEOPLE_ORGANIZATIONS,
                        element: <ClientPeopleListView />,
                        handle: {
                          title: 'People & Organizations',
                        },
                      },
                      {
                        path: 'client-team',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PEOPLE_CLIENT_TEAM,
                        element: <ClientProfessionalTeam />,
                        handle: {
                          title: 'Client Team',
                        },
                      },
                      {
                        path: 'family-tree',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_FAMILY_TREE,
                        element: <ClientFamilyTreePage />,
                        handle: {
                          title: 'Family Tree',
                        },
                      },
                    ],
                  },
                  {
                    path: 'estate-waterfall',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB,
                    lazy: lazyRoute(
                      () =>
                        import(
                          '@/pages/ClientPages/ClientWaterfallPage/ClientWaterfallPage'
                        ),
                      'ClientWaterfallPage'
                    ),
                    children: [
                      {
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.__CONTAINER,
                        lazy: lazyRoute(
                          () =>
                            import(
                              '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallBasePage/ClientDetailsEstateWaterfallBasePage'
                            ),
                          'ClientDetailsEstateWaterfallBasePage'
                        ),
                      },
                      {
                        path: ':waterfallId',
                        key: ROUTE_KEYS.__CONTAINER,
                        lazy: lazyRoute(
                          () =>
                            import(
                              '@/pages/ClientPages/ClientWaterfallPage/ClientWaterfallContexts'
                            ),
                          'ClientWaterfallContexts'
                        ),
                        children: [
                          {
                            path: 'summary',
                            key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallSummaryPage/ClientDetailsEstateWaterfallSummaryPage'
                                ),
                              'ClientDetailsEstateWaterfallSummaryPage'
                            ),
                            handle: {
                              title: 'Estate Waterfall',
                            },
                            children: [
                              {
                                path: 'estate-tax-calculation',
                                key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_SUMMARY_ESTATE_TAX_CALCULATION_TROWSER,
                                lazy: lazyRoute(
                                  () =>
                                    import(
                                      '@/modules/estateTaxScenarios/EstateTaxScenariosTrowser'
                                    ),
                                  'EstateTaxScenariosTrowserPage'
                                ),
                              },
                            ],
                          },
                          {
                            path: 'diagram',
                            key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_DIAGRAM,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallDiagramPage'
                                ),
                              'ClientDetailsEstateWaterfallDiagramPage'
                            ),
                            handle: {
                              title: 'Estate Waterfall',
                            },
                            children: [
                              {
                                path: 'estate-tax-calculation',
                                key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_DIAGRAM_ESTATE_TAX_CALCULATION_TROWSER,
                                lazy: lazyRoute(
                                  () =>
                                    import(
                                      '@/modules/estateTaxScenarios/EstateTaxScenariosTrowser'
                                    ),
                                  'EstateTaxScenariosTrowserPage'
                                ),
                              },
                            ],
                          },
                          {
                            path: 'list',
                            key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_LIST,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallSummaryListPage'
                                ),
                              'ClientDetailsEstateWaterfallSummaryListPage'
                            ),
                            handle: {
                              title: 'Estate Waterfall',
                            },
                            children: [
                              {
                                path: 'estate-tax-calculation',
                                key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_LIST_ESTATE_TAX_CALCULATION_TROWSER,
                                lazy: lazyRoute(
                                  () =>
                                    import(
                                      '@/modules/estateTaxScenarios/EstateTaxScenariosTrowser'
                                    ),
                                  'EstateTaxScenariosTrowserPage'
                                ),
                              },
                            ],
                          },
                          {
                            path: 'beneficiary-reporting',
                            key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallBeneficiaryReport/ClientDetailsEstateWaterfallBeneficiaryReport'
                                ),
                              'ClientDetailsEstateWaterfallBeneficiaryReport'
                            ),
                            handle: {
                              title: 'Beneficiary reporting',
                            },
                            children: [
                              {
                                path: '',
                                index: true,
                                key: ROUTE_KEYS.__CONTAINER,
                                element: <Navigate to="overview" replace />,
                              },
                              {
                                path: 'overview',
                                index: true,
                                key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_OVERVIEW,
                                lazy: lazyRoute(
                                  () =>
                                    import(
                                      '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallBeneficiaryReport/ClientDetailsEstateWaterfallBeneficiaryReportOverview'
                                    ),
                                  'ClientDetailsEstateWaterfallBeneficiaryReportOverview'
                                ),
                              },
                              {
                                path: ':clientOrOrgId',
                                key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_BENEFICIARY_REPORTING_BENEFICIARY_DETAILS,
                                lazy: lazyRoute(
                                  () =>
                                    import(
                                      '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallBeneficiaryReport/ClientDetailsEstateWaterfallBeneficiaryReportDetails'
                                    ),
                                  'ClientDetailsEstateWaterfallBeneficiaryReportDetails'
                                ),
                              },
                            ],
                          },
                          {
                            path: 'tax-overview',
                            key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_TAX_OVERVIEW,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/pages/ClientDetailsPage/ClientDetailsEstateWaterfallTaxOverviewPage/ClientDetailsEstateWaterfallTaxOverviewPage'
                                ),
                              'ClientDetailsEstateWaterfallTaxOverviewPage'
                            ),
                            handle: {
                              title: 'Tax overview',
                            },
                            children: [
                              {
                                path: 'estate-tax-calculation',
                                key: ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB_TAX_OVERVIEW_ESTATE_TAX_CALCULATION_TROWSER,
                                lazy: lazyRoute(
                                  () =>
                                    import(
                                      '@/modules/estateTaxScenarios/EstateTaxScenariosTrowser'
                                    ),
                                  'EstateTaxScenariosTrowserPage'
                                ),
                              },
                            ],
                          },
                          {
                            path: 'manage-dispositions',
                            handle: {
                              title: 'Dispositions',
                            },
                            key: ROUTE_KEYS.ESTATE_WATERFALL_MANAGE_DISPOSITIONS,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/ManageDispositionsPage'
                                ),
                              'ManageDispositionsPage'
                            ),
                            children: [
                              {
                                path: 'estate-tax-calculation',
                                key: ROUTE_KEYS.ESTATE_WATERFALL_MANAGE_DISPOSITIONS_ESTATE_TAX_CALCULATION_TROWSER,
                                lazy: lazyRoute(
                                  () =>
                                    import(
                                      '@/modules/estateTaxScenarios/EstateTaxScenariosTrowser'
                                    ),
                                  'EstateTaxScenariosTrowserPage'
                                ),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'presentations',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS,
                    lazy: lazyRoute(
                      () =>
                        import(
                          '@/pages/ClientPages/ClientPresentationsPage/ClientPresentationsPage'
                        ),
                      'ClientPresentationsPage'
                    ),
                    handle: {
                      title: 'Presentations',
                    },
                  },
                  {
                    path: 'presentations/user-templates',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_TEMPLATES_USER_LIST,
                    lazy: lazyRoute(
                      () =>
                        import(
                          '@/pages/ClientPages/ClientPresentationsPage/PresentationTemplateListPage/PresentationTemplateListPage'
                        ),
                      'PresentationTemplateListPage'
                    ),
                    handle: {
                      title: 'Templates',
                    },
                  },
                  {
                    path: 'presentations/user-templates/:templateId',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_TEMPLATES_USER_EDITOR,
                    lazy: lazyRoute(
                      () =>
                        import(
                          '@/pages/ClientPages/ClientPresentationsPage/PresentationTemplateEditorPage/PresentationTemplateEditorPage'
                        ),
                      'PresentationTemplateEditorPage'
                    ),
                    handle: {
                      title: 'Edit template',
                    },
                  },
                  {
                    path: 'calculators',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS,
                    lazy: lazyRoute(
                      () =>
                        import(
                          '@/pages/ClientDetailsPage/ClientDetailsProposalsPage/ClientDetailsProposalsPage'
                        ),
                      'ClientDetailsProposalsPage'
                    ),
                    handle: {
                      title: 'Calculator',
                    },
                  },
                  {
                    path: 'ownership',
                    key: ROUTE_KEYS.HOUSEHOLD_SIDEBAR_OWNERSHIP,
                    element: <ClientOwnershipPage />,
                    children: [
                      {
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.__CONTAINER,
                        element: <Navigate to="diagram" replace />,
                      },
                      {
                        path: 'diagram',
                        key: ROUTE_KEYS.HOUSEHOLD_OWNERSHIP_ENTITY_MAP,
                        lazy: lazyRoute(
                          () =>
                            import(
                              '@/pages/ClientDetailsPage/ClientDetailsEntitiesPage/ClientDetailsEntityMapPage/ClientDetailsEntityMapBasePage'
                            ),
                          'ClientDetailsEntityMapBasePage'
                        ),
                        children: [
                          {
                            path: ':id',
                            key: ROUTE_KEYS.HOUSEHOLD_OWNERSHIP_ENTITY_MAP_DIAGRAM,
                            lazy: lazyRoute(
                              () =>
                                import(
                                  '@/pages/ClientDetailsPage/ClientDetailsEntitiesPage/ClientDetailsEntityMapPage/ClientDetailsEntityMapPage'
                                ),
                              'ClientDetailsEntityMapPage'
                            ),
                            handle: {
                              title: 'Entity Map',
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'insights',
                    key: ROUTE_KEYS.HOUSEHOLD_INSIGHTS,
                    lazy: lazyRoute(
                      () => import('@/pages/InsightsPage/InsightsPage'),
                      'InsightsPage'
                    ),
                    handle: {
                      title: 'Insights',
                    },
                  },
                  {
                    path: 'performance',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PERFORMANCE,
                    element: <ClientDetailsPerformancePage />,
                    handle: {
                      title: 'Value Creation',
                    },
                  },
                  {
                    path: 'access',
                    key: ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ACCESS,
                    element: <ClientAccessPage />,
                    handle: {
                      title: 'Access',
                    },
                    children: [
                      {
                        path: 'details',
                        key: ROUTE_KEYS.MANAGE_HOUSEHOLD_DETAILS,
                        element: <KeyDetailsPage />,
                        handle: {
                          title: 'Client access',
                        },
                      },
                      {
                        path: 'admin',
                        key: ROUTE_KEYS.MANAGE_HOUSEHOLD_ADMIN,
                        lazy: () =>
                          import(
                            '@/pages/manage/editClient/ClientAdminPage/HouseholdAdminPage'
                          ),
                        handle: {
                          title: 'Admin',
                        },
                      },
                      {
                        path: 'history',
                        key: ROUTE_KEYS.HOUSEHOLD_ACTIVITY_HISTORY,
                        element: <ClientActivityHistoryPage />,
                        handle: {
                          title: 'Activity History',
                        },
                      },
                      {
                        path: 'kg-admin',
                        key: ROUTE_KEYS.HOUSEHOLD_KG_ADMIN,
                        lazy: () =>
                          import(
                            '@/pages/manage/editClient/HouseholdKGAdminPage/HouseholdKGAdminPage'
                          ),
                        handle: {
                          title: 'KG Admin',
                        },
                      },
                      {
                        // this is the catchall route, so that users who land on /households/:id/trust/:id will be
                        // redirected to this default path
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.SPECIFIC_HOUSEHOLD_REDIRECT,
                        element: <Navigate to={`details`} replace />,
                      },
                    ],
                  },
                  {
                    path: 'tasks',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_TASKS,
                    element: <ClientTasksPage />,
                    handle: {
                      title: 'Tasks',
                    },
                    children: [
                      {
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.__CONTAINER,
                        element: <ClientDetailsTasksPage />,
                      },
                    ],
                  },
                  {
                    path: 'documents',
                    key: ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
                    element: <ClientDocumentsPage />,
                    handle: {
                      title: 'Documents',
                    },
                    children: [
                      {
                        path: '',
                        index: true,
                        key: ROUTE_KEYS.__CONTAINER,
                        element: <ClientDetailsDocumentsPage />,
                      },
                      {
                        path: 'memos',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS_MEMOS,
                        element: <ClientDetailsMemosPage />,
                        handle: {
                          title: 'Memos',
                        },
                      },
                      {
                        path: ':documentId',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
                        element: (
                          <DocumentDetailsPage
                            variant={DocumentDetailsPageVariant.Household}
                          />
                        ),
                      },
                      {
                        path: 'memos/:memoId',
                        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
                        element: <MemoDetailsPage />,
                      },
                    ],
                  },
                  {
                    path: 'forms',
                    key: ROUTE_KEYS.__CONTAINER,
                    element: <HouseholdFormRoutes />,
                    children: [
                      {
                        index: true,
                        path: '',
                        key: ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSIONS,
                        lazy: lazyRoute(
                          () =>
                            import(
                              '@/modules/forms/HouseholdFormSubmissionsPage'
                            ),
                          'HouseholdFormSubmissionsPage'
                        ),
                        handle: { title: 'Reviews' },
                      },
                      {
                        path: ':submissionId',
                        key: ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSION_RESULT,
                        lazy: lazyRoute(
                          () =>
                            import(
                              '@/modules/forms/FormSubmissionAnswerReviewRoute'
                            ),
                          'FormSubmissionAnswerReviewRoute'
                        ),
                        handle: { title: 'Review' },
                      },
                    ],
                  },
                ],
              },
              {
                path: 'designer',
                key: ROUTE_KEYS.__CONTAINER,
                children: [
                  {
                    path: `:entityType/:entityId/:designerStage`,
                    key: ROUTE_KEYS.HOUSEHOLD_ENTITY_DESIGNER,
                    element: <DesignerPageV2 />,
                    handle: {
                      title: 'GRAT Designer',
                      features: {
                        hideGlobalSidebar: true,
                      },
                    },
                  },
                  {
                    path: `gifting/:proposalId`,
                    key: ROUTE_KEYS.HOUSEHOLD_GIFT_DESIGNER,
                    element: <GiftDesignerPage />,
                    handle: {
                      title: 'Gift Designer',
                      features: {
                        hideGlobalSidebar: true,
                      },
                    },
                    children: [
                      {
                        path: GiftDesignerStages.BASIC_INFORMATION,
                        key: ROUTE_KEYS.HOUSEHOLD_GIFT_DESIGNER_BASIC_INFORMATION,
                        element: <GiftDesignerBasicInformationPage />,
                      },
                      {
                        path: GiftDesignerStages.MODEL_SCENARIOS,
                        key: ROUTE_KEYS.HOUSEHOLD_GIFT_DESIGNER_MODEL_SCENARIOS,
                        element: <GiftDesignerModelScenariosPage />,
                      },
                    ],
                  },
                  {
                    path: 'charitable-trust/:variant/:proposalId',
                    key: ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER,
                    element: <CharitableTrustDesignerPage />,
                    handle: {
                      title: 'Charitable trust designer',
                      features: {
                        hideGlobalSidebar: true,
                      },
                    },
                    children: [
                      {
                        path: CharitableTrustDesignerStages.BASIC_INFORMATION,
                        key: ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION,
                        element: <CharitableTrustDesignerBasicInformation />,
                      },
                      {
                        path: CharitableTrustDesignerStages.MODEL_SCENARIOS,
                        key: ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_MODEL_SCENARIOS,
                        element: <CharitableTrustDesignerAnalysis />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'implementation',
                key: ROUTE_KEYS.__CONTAINER,
                handle: {
                  features: {
                    hideGlobalSidebar: true,
                  },
                },
                children: [
                  {
                    path: `:entityId`,
                    key: ROUTE_KEYS.HOUSEHOLD_ENTITY_IMPLEMENTATION,
                    element: <ImplementationPage />,
                    handle: {
                      title: 'Implementation',
                    },
                  },
                ],
              },
              {
                path: 'proposal',
                key: ROUTE_KEYS.__CONTAINER,
                handle: {
                  title: 'Proposals',
                },
                children: [
                  // TODO: These two routes can be compacted into one fairly trivially
                  {
                    path: `:proposalId`,
                    key: ROUTE_KEYS.HOUSEHOLD_PROPOSAL_EDIT,
                    element: <ProposalBuilderPage />,
                  },
                  {
                    path: `:proposalId`,
                    key: ROUTE_KEYS.HOUSEHOLD_PROPOSAL_CREATE,
                    element: <ProposalBuilderPage />,
                  },
                ],
              },
              {
                path: 'education',
                key: ROUTE_KEYS.EDUCATION,
                element: <EducationPage />,
              },
              {
                // this is the catchall route, so that users who land on /households/:id will be
                // redirected to this default path
                path: '',
                index: true,
                key: ROUTE_KEYS.SPECIFIC_HOUSEHOLD_REDIRECT,
                element: <Navigate to={`details/overview`} replace />,
              },
            ],
          },
        ],
      },
      {
        path: 'task',
        element: <PrimaryLayout />,
        key: ROUTE_KEYS.__CONTAINER,
        children: [
          {
            path: ':taskId',
            key: ROUTE_KEYS.TASK_STANDALONE,
            element: <StandaloneTaskPage />,
            handle: {
              title: 'Task Details',
            },
          },
        ],
      },
      {
        path: 'tasks',
        element: <PrimaryLayout />,
        key: ROUTE_KEYS.__CONTAINER,
        children: [
          {
            index: true,
            path: '',
            handle: {
              title: 'Tasks',
            },
            element: (
              <GuardedPageRedirect route={ROUTE_KEYS.TASKS}>
                <TasksPage />
              </GuardedPageRedirect>
            ),
            key: ROUTE_KEYS.TASKS,
          },
        ],
      },
      {
        path: 'collaborators',
        element: <PrimaryLayout />,
        key: ROUTE_KEYS.__CONTAINER,
        children: [
          {
            index: true,
            path: '',
            handle: {
              title: 'Collaborators',
            },
            element: (
              <GuardedPageRedirect route={ROUTE_KEYS.COLLABORATORS}>
                <CollaboratorsPage />
              </GuardedPageRedirect>
            ),
            key: ROUTE_KEYS.COLLABORATORS,
          },
        ],
      },
      {
        path: 'admin',
        element: <PrimaryLayout />,
        key: ROUTE_KEYS.__CONTAINER,
        children: [
          {
            key: ROUTE_KEYS.ADMIN,
            element: <AdminPage />,
            handle: {
              title: 'Settings',
            },
            children: [
              {
                path: `sub-brands`,
                key: ROUTE_KEYS.ADMIN_SUB_BRANDS,
                element: <AdminSubBrandsPage />,
                handle: {
                  title: 'Sub-brands',
                },
              },
              {
                path: `categories`,
                key: ROUTE_KEYS.ADMIN_CATEGORIES,
                element: <AdminCategoriesConfigurationPage />,
                handle: {
                  title: 'Categories',
                },
              },
              {
                path: `branding`,
                key: ROUTE_KEYS.ADMIN_BRANDING,
                element: <AdminBrandingPage />,
                handle: {
                  title: 'Branding',
                },
              },
              {
                path: `users`,
                key: ROUTE_KEYS.ADMIN_USERS,
                element: <AdminUsersPage />,
                handle: {
                  title: 'Users',
                },
              },
              {
                path: `forms`,
                key: ROUTE_KEYS.__CONTAINER,
                element: <AdminFormsRoutes />,
                children: [
                  {
                    index: true,
                    path: '',
                    key: ROUTE_KEYS.ADMIN_FORMS,
                    element: <AdminFormsPage />,
                    handle: {
                      title: 'Reviews',
                    },
                  },
                  {
                    path: ':formId',
                    key: ROUTE_KEYS.ADMIN_FORM_EDITOR,
                    element: <AdminFormEditorPage />,
                    handle: {
                      title: 'Review editor',
                    },
                  },
                  {
                    path: ':formId/tests',
                    key: ROUTE_KEYS.ADMIN_FORM_TESTS,
                    element: <AdminFormTestsPage />,
                    handle: { title: 'Test runs' },
                  },
                  {
                    path: ':formId/tests/:versionId',
                    key: ROUTE_KEYS.ADMIN_FORM_TEST_RUN,
                    element: <AdminFormTestRunPage />,
                    handle: { title: 'Test run' },
                  },
                  {
                    path: ':formId/tests/:versionId/submissions/:submissionId',
                    key: ROUTE_KEYS.ADMIN_FORM_TEST_SUBMISSION,
                    element: <AdminFormTestSubmissionPage />,
                    handle: { title: 'Form test submission' },
                  },
                ],
              },
              {
                path: `teams`,
                key: ROUTE_KEYS.ADMIN_TEAMS,
                element: <AdminTeamsPage />,
                handle: {
                  title: 'Teams',
                },
              },
              {
                path: `configurations`,
                key: ROUTE_KEYS.ADMIN_CONFIGURATIONS,
                element: <AdminConfigurationsPage />,
                handle: {
                  title: 'Configurations',
                },
              },
              {
                path: `feature-flags`,
                key: ROUTE_KEYS.ADMIN_FEATURE_FLAGS,
                element: <AdminFeatureFlagsPage />,
                handle: {
                  title: 'Feature Flags',
                },
              },
              {
                path: `integrations`,
                key: ROUTE_KEYS.ADMIN_INTEGRATIONS,
                element: <AdminIntegrationsPage />,
                handle: {
                  title: 'Data integrations',
                },
              },
              {
                path: 'integrations/sftp-files',
                key: ROUTE_KEYS.ADMIN_SFTP_FILES,
                element: <ManagedSFTPFilesPage />,
                handle: { title: 'Managed SFTP files' },
              },
              {
                path: `organizations`,
                key: ROUTE_KEYS.ADMIN_ORGANIZATIONS,
                element: <AdminOrganizationsPage />,
                handle: {
                  title: 'Organizations',
                },
              },
              {
                path: `tenants`,
                key: ROUTE_KEYS.ADMIN_TENANTS,
                element: <AdminTenantsPage />,
                handle: {
                  title: 'Tenants',
                },
              },
              {
                path: `audit-log`,
                key: ROUTE_KEYS.ADMIN_AUDIT_LOG,
                element: <AdminAuditLogPage />,
                handle: {
                  title: 'Audit log',
                },
              },
              {
                path: `legal`,
                key: ROUTE_KEYS.ADMIN_LEGAL_DISCLAIMERS,
                element: <AdminLegalDisclaimersPage />,
                handle: {
                  title: 'Legal disclaimers',
                },
              },
              {
                path: `roles`,
                key: ROUTE_KEYS.ADMIN_ROLES,
                element: <AdminRolesPage />,
                handle: {
                  title: 'Roles',
                },
              },
              {
                path: `security`,
                key: ROUTE_KEYS.ADMIN_SECURITY,
                element: <AdminSecurityPage />,
                handle: {
                  title: 'Security',
                },
              },
              {
                path: `summary-prompts`,
                key: ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS,
                element: <AdminSummaryPromptsPage />,
                handle: {
                  title: 'Summary prompts',
                },
              },
              {
                path: `summary-prompts/:id`,
                key: ROUTE_KEYS.ADMIN_PROMPT_ITERATION,
                element: <AdminPromptIterationPage />,
                handle: {
                  title: 'Prompt details',
                },
              },
              {
                path: 'presentation-templates',
                key: ROUTE_KEYS.ADMIN_PRESENTATION_TEMPLATES,
                element: <AdminPresentationTemplatesPage />,
                handle: {
                  title: 'Presentation templates',
                },
              },
              {
                path: `presentation-templates/:templateId`,
                key: ROUTE_KEYS.ADMIN_PRESENTATION_TEMPLATE_EDITOR,
                element: <AdminPresentationTemplateEditorPage />,
                handle: {
                  title: 'Presentation template editor',
                },
              },
              {
                path: `education`,
                key: ROUTE_KEYS.ADMIN_EDUCATION,
                element: <AdminEducationPage />,
                handle: {
                  title: 'Education',
                },
                children: [
                  {
                    path: `:slideId`,
                    key: ROUTE_KEYS.ADMIN_EDUCATION_SLIDE,
                    element: <AdminEducationPage />,
                    handle: {
                      title: 'Education slide',
                    },
                  },
                ],
              },
              {
                path: 'reporting/document-summaries',
                key: ROUTE_KEYS.ADMIN_DOCUMENT_SUMMARIES_REPORT,
                element: (
                  <GuardedPageRedirect
                    route={ROUTE_KEYS.ADMIN_DOCUMENT_SUMMARIES_REPORT}
                  >
                    <AdminDocumentSummariesReportPage />
                  </GuardedPageRedirect>
                ),
                handle: {
                  title: 'Document summaries report',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'internal',
        key: ROUTE_KEYS.__CONTAINER,
        element: <PrimaryLayout />,
        children: [
          {
            key: ROUTE_KEYS.__CONTAINER,
            // Code splitting this route so that we don't bundle the dev tools page with the rest of the app
            lazy: () => import('@/pages/internal/InternalToolsPage'),
            handle: {
              title: 'Internal Tools',
            },
            children: [
              {
                path: `dev-tools`,
                key: ROUTE_KEYS.INTERNAL_DEV_TOOLS,
                lazy: lazyRoute(
                  () => import('@/pages/internal/DevToolsPage/DevToolsPage'),
                  'DevToolsPage'
                ),
                handle: {
                  title: 'Dev Tools',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'user',
        element: <PrimaryLayout />,
        key: ROUTE_KEYS.__CONTAINER,
        children: [
          {
            key: ROUTE_KEYS.ADMIN,
            element: <UserProfilePage />,
            children: [
              {
                path: `settings`,
                key: ROUTE_KEYS.USER_SETTINGS,
                element: <UserProfileSettingsPage />,
                handle: {
                  title: 'Profile',
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <NoSidebarLayout />,
    key: ROUTE_KEYS.__CONTAINER,
    children: [
      {
        path: CLIENT_PROPOSAL_PATH,
        key: ROUTE_KEYS.__CONTAINER,
        children: [
          {
            path: `:proposalId`,
            key: ROUTE_KEYS.HOUSEHOLD_PROPOSAL_PRESENTATION,
            lazy: lazyRoute(
              () => import('@/pages/client-proposal/PresentationPage'),
              'PresentationPage'
            ),
          },
        ],
      },
      {
        path: 'client-presentations/:householdId/presentations-v2/:presentationId',
        key: ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/ClientDetailsPage/ClientDetailsPresentationPage/ClientDetailsPresentationV2Page'
            ),
          'ClientDetailsPresentationV2Page'
        ),
      },
      {
        // Document knowledge graph standalone page
        path: 'documents/:documentId/knowledge-graph',
        key: ROUTE_KEYS.DOCUMENT_KNOWLEDGE_GRAPH,
        lazy: () =>
          import('@/pages/knowledge-graph/DocumentKnowledgeGraphPage'),
        handle: {
          title: 'Knowledge Graph',
        },
      },
      {
        // Document knowledge graph V2 standalone page (requires householdId for the query)
        path: 'households/:householdId/documents/:documentId/knowledge-graph-v2',
        key: ROUTE_KEYS.DOCUMENT_KNOWLEDGE_GRAPH_V2,
        lazy: () =>
          import('@/pages/knowledge-graph/DocumentKnowledgeGraphV2Page'),
        handle: {
          title: 'Knowledge Graph V2',
        },
      },
    ],
  },
  {
    element: <PrintOnlyLayout />,
    key: ROUTE_KEYS.__CONTAINER,
    path: '__print',
    children: [
      {
        path: 'households/:householdId/forms/:submissionId',
        key: ROUTE_KEYS.__PRINT_ONLY_FORM_REVIEW,
        lazy: lazyRoute(
          () => import('@/pages/printOnly/PrintFormAnswerReview'),
          'PrintFormAnswerReview'
        ),
      },
      {
        path: 'households/:householdId/entity-with-notes/:entityId',
        key: ROUTE_KEYS.__PRINT_ONLY_SINGLE_ENTITY_WITH_NOTES,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlySingleEntityWithNotes/PrintOnlySingleEntityWithNotes'
            ),
          'PrintOnlySingleEntityWithNotes'
        ),
      },
      {
        path: 'households/:householdId/details/documents/:documentId/:summaryId',
        key: ROUTE_KEYS.__PRINT_ONLY_DOCUMENT_SUMMARY_EXPORT_V2,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyDocumentSummary/PrintOnlyDocumentSummary'
            ),
          'PrintOnlyDocumentSummaryV2'
        ),
      },
      {
        path: 'households/:householdId/memos/:memoId',
        key: ROUTE_KEYS.__PRINT_ONLY_MEMO_EXPORT,
        lazy: lazyRoute(
          () => import('@/pages/printOnly/PrintOnlyMemo/PrintOnlyMemo'),
          'PrintOnlyMemo'
        ),
      },
      {
        path: 'households/:householdId/presentation-v2/:presentationId',
        key: ROUTE_KEYS.__PRINT_ONLY_CLIENT_PRESENTATION_V2,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyClientPresentationV2/PrintOnlyClientPresentationV2'
            ),
          'PrintOnlyClientPresentationV2'
        ),
      },
      {
        path: 'households/:householdId/proposal/:proposalId',
        key: ROUTE_KEYS.__PRINT_ONLY_CLIENT_PROPOSAL,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyClientProposal/PrintOnlyClientProposal'
            ),
          'PrintOnlyClientProposal'
        ),
      },
      {
        path: 'households/:householdId/reporting/performance',
        key: ROUTE_KEYS.__PRINT_ONLY_CLIENT_REPORTING,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyClientDashboard/PrintOnlyClientDashboard'
            ),
          'PrintOnlyClientDashboard'
        ),
      },
      {
        path: 'households/:householdId/waterfall/:hypotheticalWaterfallId/comparison',
        key: ROUTE_KEYS.__PRINT_ONLY_WATERFALL_COMPARISON_LEGACY,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyWaterfallComparison/PrintOnlyWaterfallComparison'
            ),
          'PrintOnlyWaterfallComparison'
        ),
      },
      {
        path: 'households/:householdId/waterfall/comparison',
        key: ROUTE_KEYS.__PRINT_ONLY_WATERFALL_COMPARISON,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyWaterfallComparison/PrintOnlyWaterfallComparison'
            ),
          'PrintOnlyWaterfallComparison'
        ),
      },
      {
        path: 'households/:householdId/insights',
        key: ROUTE_KEYS.__PRINT_ONLY_ESTATE_INSIGHTS,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyEstateInsights/PrintOnlyEstateInsights'
            ),
          'PrintOnlyEstateInsights'
        ),
      },
      {
        path: 'households/:householdId/education/:activeSlide',
        key: ROUTE_KEYS.__PRINT_ONLY_EDUCATION_SLIDE,
        lazy: lazyRoute(
          () =>
            import(
              '@/pages/printOnly/PrintOnlyEducationSlide/PrintOnlyEducationSlide'
            ),
          'PrintOnlyEducationSlide'
        ),
      },
    ],
  },
  {
    // this is the global default path. users that land on the root of the app
    // will hit this path.
    path: '',
    key: ROUTE_KEYS.ROOT_REDIRECT,
    element: <RootRedirect />,
    errorElement: <RootErrorBoundary />,
  },
  {
    // this is backwards-compatibility for the removal of the "home" page
    path: 'home',
    key: ROUTE_KEYS.ROOT_REDIRECT,
    element: <RootRedirect />,
  },
  {
    path: 'error/:errorCode',
    key: ROUTE_KEYS.PUBLIC_ERROR,
    element: <PublicErrorPage />,
  },
  {
    path: 'redirect/notification/:notificationKind/:householdId?/:objectId?',
    key: ROUTE_KEYS.NOTIFICATION_REDIRECT,
    element: <NotificationRedirect />,
  },
  {
    // this path is used as a centralized interface to handle redirects from the backend. this way,
    // we have a minimal, standardized API that our backend is aware of rather than it knowing about
    // all the routes and their structure.
    path: 'redirect/:destination',
    key: ROUTE_KEYS.FUNCTIONAL_REDIRECT,
    element: <FunctionalRedirect />,
  },
  {
    // this is a catchall 404 page for requests that don't map anywhere else.
    // THIS SHOULD ALWAYS COME LAST IN THE ROUTES LIST.
    path: '/:default',
    key: ROUTE_KEYS.NOT_FOUND,
    element: <Navigate to={`/error/${ErrorCodes.NOT_FOUND}`} />,
  },
];

export const routes: AugmentedRoute[] = [
  {
    element: <AppRootLayout />,
    key: ROUTE_KEYS.__CONTAINER,
    children: withLazyRouteErrorBoundaries(appRoutes),
  },
];

export const computedRoutes = computeRoutes(routes);
